import type { HTMLMotionProps } from 'motion/react'
import { motion } from 'motion/react'
import clsx from 'clsx'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'

type ButtonCurvedProps = Omit<HTMLMotionProps<'button'>, 'color'> & {
    label: string
    labelGraphicSrc?: string
}

const DEFAULT_LABEL_GRAPHIC_SRC = '/images/buttons/ReadyText.svg'

export default function ButtonCurved({
    label,
    labelGraphicSrc = DEFAULT_LABEL_GRAPHIC_SRC,
    className,
    type = 'button',
    ...buttonProps
}: ButtonCurvedProps) {
    const isStele = useSteleBreakpoint()
    const animateY = isStele ? -20 : -6
    const whileTapY = isStele ? -4 : -2

    return (
        <div
            className={clsx(
                'inline-flex relative justify-center items-center rotate-2',
                'w-57.75 h-15.25 p-0.5',
                'stele:w-231 stele:h-61 stele:p-2',
            )}
        >
            <svg
                className='absolute inset-0 size-full'
                viewBox='0 0 231 61'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                aria-hidden
            >
                <path d='M0.481084 39.5865C-2.25122 25.5299 6.92026 11.7217 21.2545 9.46731C83.4216 -0.308918 146.723 -0.485625 208.944 8.94387C223.291 11.1182 232.539 24.8749 229.885 38.9468C227.217 53.095 213.641 62.2128 199.596 60.1245C143.656 51.8066 86.7802 51.9648 30.8873 60.5943C16.8549 62.7606 3.22845 53.7191 0.481084 39.5865Z' fill='#54687C' />
                <path d='M4.40739 36.8226C2.08903 24.8956 9.87371 13.3062 21.8764 11.4187C83.6392 1.70607 146.529 1.53063 208.345 10.8988C220.358 12.7194 228.207 24.2656 225.955 36.2056C223.703 48.1454 212.203 55.9545 200.185 54.1675C143.847 45.7904 86.5673 45.9497 30.2773 54.6404C18.2693 56.4944 6.72574 48.7495 4.40739 36.8226Z' fill='#0A2897' />
                <path d='M0.481084 37.5865C-2.25122 23.5299 6.92026 9.72172 21.2545 7.46731C83.4216 -2.30892 146.723 -2.48563 208.944 6.94387C223.291 9.11819 232.539 22.8749 229.885 36.9468C227.217 51.095 213.641 60.2128 199.596 58.1245C143.656 49.8066 86.7802 49.9648 30.8873 58.5943C16.8549 60.7606 3.22845 51.7191 0.481084 37.5865ZM21.8766 11.4185C9.87389 13.306 2.08949 24.8958 4.40784 36.8228L4.52308 37.3784C7.08677 48.9892 18.4567 56.465 30.277 54.6402C86.5668 45.9494 143.847 45.7905 200.184 54.1675L200.748 54.2446C212.56 55.7097 223.738 47.9588 225.955 36.2056C228.171 24.4523 220.601 13.081 208.905 10.9917L208.344 10.8989C147.494 1.67718 85.6039 1.70272 24.7721 10.9702L21.8766 11.4185Z' fill='url(#button-curved-bottom-gradient)' />
                <defs>
                    <linearGradient id='button-curved-bottom-gradient' x1='115.121' y1='51.9907' x2='115.121' y2='4.00127' gradientUnits='userSpaceOnUse'>
                        <stop stopColor='#C8E3F7' />
                        <stop offset='1' stopColor='white' />
                    </linearGradient>
                </defs>
            </svg>

            <motion.button
                {...buttonProps}
                type={type}
                initial={false}
                animate={{ y: animateY }}
                whileTap={{ y: whileTapY }}
                transition={{
                    y: {
                        type: 'spring',
                        stiffness: 520,
                        damping: 30,
                        mass: 0.7,
                    },
                }}
                className={clsx(
                    'absolute inset-0 flex justify-center items-center bg-transparent',
                    className,
                )}
                aria-label={label}
            >
                <svg
                    className='absolute inset-0 size-full'
                    viewBox='0 0 231 61'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    aria-hidden
                >
                    <path d='M4.40836 37.8226C2.09001 25.8956 9.87469 14.3062 21.8774 12.4187C83.6401 2.70607 146.53 2.53063 208.346 11.8988C220.359 13.7194 228.208 25.2656 225.956 37.2056C223.704 49.1454 212.204 56.9545 200.186 55.1675C143.848 46.7904 86.5682 46.9497 30.2783 55.6404C18.2703 57.4944 6.72672 49.7495 4.40836 37.8226Z' fill='url(#button-curved-top-gradient)' />
                    <path d='M4.40935 37.823C2.09099 25.8961 9.8754 14.3063 21.8781 12.4188C83.6407 2.70616 146.53 2.53109 208.346 11.8992L208.906 11.992C220.417 14.0481 227.932 25.0938 226.055 36.6463L225.956 37.2059C223.739 48.959 212.562 56.71 200.749 55.2449L200.186 55.1678C144.729 46.9217 88.3584 46.9475 32.9181 55.24L30.2785 55.6404C18.4582 57.4653 7.08828 49.9895 4.52458 38.3787L4.40935 37.823ZM22.1886 14.3953C11.3519 16.0995 4.26099 26.5792 6.37224 37.4412C8.47619 48.2651 18.9783 55.3615 29.9738 53.6639C86.4623 44.9425 143.944 44.7827 200.481 53.1893C211.485 54.8254 221.948 47.6711 223.991 36.8357C226.042 25.9617 218.893 15.5205 208.046 13.8768C146.433 4.5393 83.7491 4.71448 22.1886 14.3953Z' fill='#0A2897' />
                    <path d='M15.17 26.5039C14.4789 22.1401 17.4556 18.0418 21.8194 17.3506C83.6947 7.55039 146.727 7.55039 208.603 17.3506C212.966 18.0417 215.944 22.1401 215.253 26.5039C214.562 30.8677 210.463 33.8453 206.1 33.1543C145.883 23.6168 84.5393 23.6168 24.3223 33.1543C19.9586 33.8452 15.8611 30.8677 15.17 26.5039Z' fill='url(#button-curved-glow)' />
                    <defs>
                        <linearGradient id='button-curved-top-gradient' x1='110.579' y1='59.4182' x2='108.421' y2='5.4505' gradientUnits='userSpaceOnUse'>
                            <stop stopColor='#599EFF' />
                            <stop offset='1' stopColor='#006AFF' />
                        </linearGradient>
                        <radialGradient id='button-curved-glow' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(115.211 603.251) rotate(-90) scale(592.25 592.25)'>
                            <stop offset='0.97531' stopColor='white' stopOpacity='0' />
                            <stop offset='1' stopColor='white' stopOpacity='0.35' />
                        </radialGradient>
                    </defs>
                </svg>

                <div
                    className={clsx(
                        'z-10 relative flex justify-center items-center pointer-events-none',
                        'w-27 h-7',
                        'stele:w-107.5 stele:h-27.5',
                    )}
                >
                    <img
                        src={labelGraphicSrc}
                        alt=''
                        aria-hidden
                        className='mb-2 stele:mb-6 max-w-full max-h-[75%] object-cover'
                    />
                </div>
            </motion.button>
        </div>
    )
}
